import { useCallback, useEffect, useRef, useState } from "react";
// @ts-ignore
import { Spinner } from "@aqar-app/aqar-design-system";
// @ts-ignore
import { uploadFile } from "@aqar-app/aqar-shared-lib";
import css from "./media.module.scss";
import {
  BucketFolders,
  FileExtensions,
  UploadFileTypes,
} from "../../core/interfaces/interfaces";
import { logger } from "@sentry/utils";
import config from "@/common/config";
import { Icon } from "@/components/icon";

interface ImageUploadProps {
  files?: FileList;
  name?: string;
  folder?: BucketFolders;
  file_type?: UploadFileTypes;
  file_extension?: FileExtensions;
  placeholder?: string;
  multiple?: boolean;
  mime?: "video/*" | "image/*" | "application/pdf";
  token: string;
  note: string;
  onChange?: (images: string[], files: FileList) => void;
  onStart?: () => void;
}

export const uploadImg = (
  file: File | null,
  token: string,
  folder: BucketFolders = "props",
  file_type: UploadFileTypes = "image",
  file_extension: FileExtensions = "jpg"
): Promise<{ fileName: string; uploadResponse: Response }> => {
  if (!file) return Promise.reject("NoFile");
  return uploadFile({
    getSignedUrlOptions: {
      folder,
      file_type,
      file_extension,
    },
    token,
    file,
    url: config.GQL_AUTH_EP || "",
  });
};

export function MediaUpload({
  files: f,
  token,
  onChange,
  folder,
  file_type,
  file_extension,
  multiple,
  onStart,
  mime = "image/*",
  placeholder,
  note,
  name,
  ...props
}: ImageUploadProps) {
  // @ts-ignore
  const [files, setFiles] = useState<FileList>(f || { length: 0 });
  const filenames = useRef<string[]>([]);
  const fileAsUrl = useRef<any>({});
  const dt = useRef<DataTransfer>();
  const filesInput = useRef<HTMLInputElement>(null);
  const textInput = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState<{ [key: string | number]: any }>({});

  useEffect(() => {
    logger.log({ loading });
  }, [loading]);
  const onFiles = useCallback(
    async function onFiles(newFiles?: FileList | null) {
      if (!newFiles || !filesInput.current) return;
      if (typeof onStart == "function") onStart();
      const newDt = new DataTransfer();
      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        if (!file) continue;
        newDt.items.add(file);
      }
      for (let i = 0; i < newFiles.length; i++) {
        try {
          const file = newFiles.item(i);

          if (!file) continue;
          newDt.items.add(file);
          fileAsUrl.current[file.name] = URL.createObjectURL(file);
        } catch (e) {}
      }
      filesInput.current.files = newDt.files;
      const filesLength = files.length;
      setFiles(newDt.files);

      dt.current?.clearData();
      dt.current = newDt;

      const fns: Promise<any>[] = [];
      for (let i = 0; i < newFiles.length; i++) {
        try {
          const file = newFiles.item(i);
          if (!file) continue;
          const idx = i + filesLength;
          setLoading((v) => ({ ...v, [idx]: true }));
          fns.push(
            uploadImg(file, token, folder, file_type, file_extension).then(
              ({ fileName }) => {
                setLoading((v) => ({ ...v, [idx]: false }));
                filenames.current = filenames.current.concat(fileName);
                if (!textInput.current) return;
                textInput.current.value = filenames.current.join(",");
              }
            )
          );
        } catch (e) {
          logger.log(e);
        }
      }
      await Promise.all(fns);
      textInput.current?.dispatchEvent(new Event("keyup"));
      onChange?.(filenames.current, newDt.files);
    },
    [file_extension, file_type, files, folder, onChange, onStart, token]
  );

  useEffect(() => {
    window.addEventListener("paste", (e: any) => {
      if (filesInput.current !== document.activeElement) return;
      onFiles(e?.clipboardData?.files);
      logger.log(e);
    });
    return () => {
      window.removeEventListener("paste", logger.log);
    };
  }, [onFiles]);
  const inputJSX = (
    <input
      type="file"
      multiple={multiple}
      title={``}
      accept={mime || "image/*"}
      ref={filesInput}
      onChange={(e) => onFiles(e.target?.files)}
      className={`${css.opacity0} ${css.bgRed400} ${css.left0} ${css.top0} ${css.wFull} ${css.hFull} ${css.absolute}`}
    />
  );
  useEffect(() => {
    textInput.current?.addEventListener("loadeddata", logger.log);
  }, []);
  useEffect(() => {
    if (!f) return;

    const newDt = new DataTransfer();
    for (let i = 0; i < f.length; i++) {
      if (!f || !f.item) continue;
      const file = f.item(i);
      if (!file) continue;
      fileAsUrl.current[file.name] = URL.createObjectURL(file);

      newDt.items.add(file);
    }
    dt.current = newDt;
    setFiles(f);
  }, [f]);
  // @ts-ignore
  return (
    <>
      <input ref={textInput} hidden name={name} {...props} type="text" />
      {new Array(files.length)
        .fill(0)
        .map(function (_, i): File {
          return files.item(i) as any;
        })
        .map((file, index) => {
          if (mime == "image/*")
            return (
              <div
                key={file.name + index}
                className={`${css.h32} ${css.relative} ${css.rounded} ${css.bgCenter} ${css.bgCover} ${css.border} ${css.shadowSm} ${css.bgNoRepeat}`}
                style={{
                  backgroundImage: `url(${fileAsUrl.current[file.name]})`,
                }}
              >
                {(!files.length || !multiple) && inputJSX}
                {loading[index] && (
                  <div className="absolute w-full h-full flex items-center justify-center left-0 top-0 bg-black bg-opacity-20">
                    <Spinner size="larger" />
                  </div>
                )}
                <button
                  type="button"
                  className="absolute text-white bg-black bg-opacity-40 cursor-pointer rounded p-1 bottom-1 right-1 w-8 h-8"
                  onClick={() => {
                    if (!filesInput.current || !dt.current) return;

                    const newDt = new DataTransfer();
                    for (let i = 0; i < files.length; i++) {
                      const file = files.item(i);
                      if (!file) continue;
                      if (index != i) {
                        newDt.items.add(file);
                      }
                    }
                    filesInput.current.files = newDt.files;
                    setFiles(newDt.files);
                    dt.current?.clearData();
                    dt.current = newDt;

                    filenames.current = filenames.current.filter(
                      (_x, j) => j != index,
                    );
                    onChange?.(filenames.current, newDt.files);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                    />
                  </svg>
                </button>
              </div>
            );

          if (mime === "video/*")
            return (
              <div
                key={file.name + index}
                className={`${css.h32} ${css.relative} ${css.rounded} ${css.bgCenter} ${css.bgCover} ${css.border} ${css.shadowSm} ${css.bgNoRepeat}`}
              >
                {(!files.length || !multiple) && inputJSX}

                <video
                  key={file.name + index}
                  className="h-32 lg:h-60 relative rounded bg-center bg-cover bg-no-repeat"
                  src={fileAsUrl.current[file.name]}
                  autoPlay
                  controls
                />
                {loading[index] && (
                  <div className="absolute w-full h-full flex items-center justify-center left-0 top-0 bg-black bg-opacity-20">
                    <Spinner size="larger" />
                  </div>
                )}
                <button
                  type="button"
                  className="absolute text-white bg-black bg-opacity-40 cursor-pointer rounded p-1 bottom-1 right-1 w-8 h-8"
                  onClick={() => {
                    if (!filesInput.current || !dt.current) return;
                    const newDt = new DataTransfer();
                    for (let i = 0; i < files.length; i++) {
                      const file = files.item(i);
                      if (!file) continue;
                      if (index != i) {
                        newDt.items.add(file);
                      }
                    }
                    filesInput.current.files = newDt.files;
                    setFiles(newDt.files);
                    dt.current?.clearData();
                    dt.current = newDt;

                    filenames.current = filenames.current.filter(
                      (_x, j) => j != index,
                    );
                    onChange?.(filenames.current, newDt.files);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                    />
                  </svg>
                </button>
              </div>
            );
          return (
            <div
              key={file.name + index}
              className={`${css.relative} ${css.mt4} ${css.borderDashed} ${css.textGray400} ${css.gap2} ${css.borderGray200} ${css.border2} ${css.rounded} ${css.h32} ${css.flex} ${css.flexCol} ${css.justifyCenter} ${css.itemsCenter}`}
            >
              {(!files.length || !multiple) && inputJSX}
              <Icon width={20} height={20} name="Agreement-Pdf" />
              {file.name}
              <button
                type="button"
                className={`${css.textBlack} ${css.bgOpacity40} ${css.cursorPointer} ${css.rounded} ${css.p1} ${css.w6} ${css.h6}`}
                onClick={() => {
                  if (!filesInput.current || !dt.current) return;
                  const newDt = new DataTransfer();
                  for (let i = 0; i < files.length; i++) {
                    const file = files.item(i);
                    if (!file) continue;
                    if (index != i) {
                      newDt.items.add(file);
                    }
                  }
                  filesInput.current.files = newDt.files;
                  setFiles(newDt.files);
                  dt.current?.clearData();
                  dt.current = newDt;

                  filenames.current = filenames.current.filter(
                    (_x, j) => j != index,
                  );
                  onChange?.(filenames.current, newDt.files);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                  />
                </svg>
              </button>
            </div>
          );
        })}
      {(!files.length || multiple) && (
        <div className={`${css.relative} ${css.mt4}`}>
          {inputJSX}
          <div
            className={`${css.borderDashed} ${css.textGray400} ${css.gap2} ${css.borderGray200} ${css.border2} ${css.rounded} ${css.h32} ${css.flex} ${css.flexCol} ${css.justifyCenter} ${css.itemsCenter}`}
          >
            {mime == "image/*" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                />
              </svg>
            )}
            {mime === "video/*" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
                />
              </svg>
            )}
            {mime === "application/pdf" && (
              <Icon
                width={30}
                height={30}
                className={`${css.w12} ${css.h12}`}
                name="uploadMedia"
              />
            )}
            {placeholder || "Add Media"}
            <small className={css.small}>{note || ""}</small>
            <div className={css.textXs}>
              <div
                className={`${css.textCenter} ${css.itemsCenter} ${css.flex} ${css.gap1} ${css.textZinc300} ${css.fontBold}`}
                dir="ltr"
              >
                <small
                  className={`${css.small} ${css.inlineFlex} ${css.itemsCenter} ${css.justifyCenter} ${css.textZinc300} ${css.bgZinc500} ${css.rounded} ${css.p2} ${css.w10} ${css.h5}`}
                >
                  CTRL
                </small>
                <small className={`${css.textZinc500} ${css.small}`}>+</small>
                <small
                  className={`${css.small} ${css.inlineFlex} ${css.itemsCenter} ${css.justifyCenter}  ${css.textZinc300} ${css.bgZinc500} ${css.rounded} ${css.p2} ${css.w6} ${css.h5}`}
                >
                  V
                </small>
              </div>
              {/*<div className={`${css.flex} ${css.gap2} ${css.mt4} ${css.itemsCenter} ${css.justifyCenter}`}>*/}
              {/*  <svg*/}
              {/*    xmlns="http://www.w3.org/2000/svg"*/}
              {/*    fill="none"*/}
              {/*    viewBox="0 0 24 24"*/}
              {/*    strokeWidth={1.5}*/}
              {/*    stroke="currentColor"*/}
              {/*    className="w-6 h-6"*/}
              {/*  >*/}
              {/*    <path*/}
              {/*      strokeLinecap="round"*/}
              {/*      strokeLinejoin="round"*/}
              {/*      d="M12 9.75v6.75m0 0l-3-3m3 3l3-3m-8.25 6a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"*/}
              {/*    />*/}
              {/*  </svg>*/}
              {/*  <p>Drop Files</p>*/}
              {/*</div>*/}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
